import * as THREE from "three/webgpu";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { createToonGradient } from "./toon";

type AvatarState = "idle" | "run" | "jump" | "hit" | "celebrate";

const CLIPS: Record<AvatarState, string> = {
  idle: "PF_Idle",
  run: "PF_Sprint",
  jump: "PF_JumpStart",
  hit: "PF_HitKnockback",
  celebrate: "PF_DoubleJumpLand",
};

export class PajamaAvatar {
  readonly root = new THREE.Group();
  readonly shadow: THREE.Mesh;
  readonly #toonGradient = createToonGradient();
  #model: THREE.Object3D | null = null;
  #mixer: THREE.AnimationMixer | null = null;
  #clips = new Map<string, THREE.AnimationClip>();
  #action: THREE.AnimationAction | null = null;
  #state: AvatarState = "idle";
  #hitSeconds = 0;
  #loaded = false;

  constructor(scene: THREE.Scene) {
    this.root.name = "pillowfight-runner";
    scene.add(this.root);
    this.shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.82, 32),
      new THREE.MeshBasicMaterial({
        color: 0x18352f,
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
      }),
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.025;
    this.root.add(this.shadow);
    this.#buildLoadingStandIn();
  }

  get loaded(): boolean {
    return this.#loaded;
  }

  async load(slug = "capybara"): Promise<void> {
    const loader = new GLTFLoader();
    const [character, animations] = await Promise.all([
      loader.loadAsync(`./assets/characters/${slug}.glb`),
      loader.loadAsync("./assets/animations/PF_GameplayAnimations.glb"),
    ]);
    this.root.children.filter((child) => child.userData.loadingStandIn).forEach((child) => child.removeFromParent());
    const model = character.scene;
    model.name = `PF_${slug}`;
    model.rotation.y = Math.PI;
    model.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const toonMaterials = materials.map((material) => toonFromMaterial(material, this.#toonGradient));
        mesh.material = Array.isArray(mesh.material) ? toonMaterials : toonMaterials[0]!;
      }
    });

    const bounds = new THREE.Box3().setFromObject(model);
    const size = bounds.getSize(new THREE.Vector3());
    const scale = 1.58 / Math.max(0.1, size.y);
    model.scale.setScalar(scale);
    bounds.setFromObject(model);
    const center = bounds.getCenter(new THREE.Vector3());
    model.position.set(-center.x, -bounds.min.y, -center.z);
    this.root.add(model);
    this.#model = model;
    const basePose = preparePillowFightBasePose(model, character.animations[0]);
    this.#mixer = new THREE.AnimationMixer(model);
    animations.animations.forEach((clip) => this.#clips.set(clip.name, buildSafePillowClip(clip, basePose)));
    this.#loaded = true;
    this.setState("run", true);
  }

  setState(state: AvatarState, immediate = false): void {
    if (!this.#mixer || !this.#model) return;
    if (state === this.#state && this.#action?.isRunning()) return;
    const clip = this.#clips.get(CLIPS[state]);
    if (!clip) return;
    const next = this.#mixer.clipAction(clip, this.#model);
    next.reset();
    next.enabled = true;
    next.setEffectiveTimeScale(state === "run" ? 1.18 : 1);
    next.setEffectiveWeight(1);
    if (state === "hit" || state === "jump" || state === "celebrate") {
      next.setLoop(THREE.LoopOnce, 1);
      next.clampWhenFinished = true;
    } else {
      next.setLoop(THREE.LoopRepeat, Infinity);
    }
    next.play();
    if (this.#action && this.#action !== next) {
      if (immediate) this.#action.stop();
      else this.#action.crossFadeTo(next, 0.14, false);
    }
    this.#action = next;
    this.#state = state;
    if (state === "hit") this.#hitSeconds = 0.72;
  }

  update(dt: number, time: number, x: number, y: number, shield: boolean): void {
    this.#mixer?.update(dt);
    this.#hitSeconds = Math.max(0, this.#hitSeconds - dt);
    if (this.#hitSeconds === 0 && this.#state === "hit") this.setState("run");
    this.root.position.x = THREE.MathUtils.damp(this.root.position.x, x, 13, dt);
    this.root.position.y = y;
    this.root.rotation.z = THREE.MathUtils.damp(this.root.rotation.z, (x - this.root.position.x) * -0.12, 10, dt);
    this.root.rotation.y = Math.sin(time * 0.7) * 0.025;
    this.shadow.scale.setScalar(THREE.MathUtils.clamp(1 - y * 0.12, 0.64, 1));
    (this.shadow.material as THREE.MeshBasicMaterial).opacity = shield ? 0.48 : 0.28;
    (this.shadow.material as THREE.MeshBasicMaterial).color.set(shield ? 0x5debd0 : 0x18352f);
  }

  hit(): void {
    this.setState("hit", true);
  }

  jump(): void {
    this.setState("jump", true);
  }

  run(): void {
    if (this.#hitSeconds <= 0) this.setState("run");
  }

  celebrate(): void {
    this.setState("celebrate", true);
  }

  #buildLoadingStandIn(): void {
    const material = new THREE.MeshToonMaterial({ color: 0xc18a64, gradientMap: this.#toonGradient });
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.52, 0.7, 6, 12), material);
    body.position.y = 1.05;
    body.castShadow = true;
    body.userData.loadingStandIn = true;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.58, 16, 12), material);
    head.position.set(0, 1.85, -0.04);
    head.scale.set(1.16, 0.86, 0.96);
    head.castShadow = true;
    head.userData.loadingStandIn = true;
    this.root.add(body, head);
  }
}

function toonFromMaterial(source: THREE.Material, gradientMap: THREE.Texture): THREE.MeshToonMaterial {
  const material = source as THREE.MeshStandardMaterial;
  const toon = new THREE.MeshToonMaterial({
    color: material.color?.clone() ?? new THREE.Color(0xffffff),
    map: material.map ?? null,
    normalMap: material.normalMap ?? null,
    alphaMap: material.alphaMap ?? null,
    emissive: material.emissive?.clone() ?? new THREE.Color(0x000000),
    emissiveMap: material.emissiveMap ?? null,
    emissiveIntensity: material.emissiveIntensity ?? 1,
    transparent: material.transparent,
    opacity: material.opacity,
    alphaTest: material.alphaTest,
    side: material.side,
    vertexColors: material.vertexColors,
    gradientMap,
  });
  source.dispose();
  return toon;
}

function preparePillowFightBasePose(
  model: THREE.Object3D,
  rigCheck: THREE.AnimationClip | undefined,
): Map<string, THREE.Quaternion> {
  const bones = new Map<string, THREE.Object3D>();
  model.traverse((object) => {
    if (object.name.startsWith("PF_")) bones.set(object.name, object);
  });
  const rest = new Map<string, THREE.Quaternion>();
  const grab = new Map<string, THREE.Quaternion>();
  if (rigCheck) {
    const mixer = new THREE.AnimationMixer(model);
    const action = mixer.clipAction(rigCheck).play();
    mixer.setTime(0);
    bones.forEach((bone, name) => rest.set(name, bone.quaternion.clone()));
    mixer.setTime(35 / 30);
    bones.forEach((bone, name) => grab.set(name, bone.quaternion.clone()));
    action.stop();
    mixer.uncacheRoot(model);
  } else {
    bones.forEach((bone, name) => rest.set(name, bone.quaternion.clone()));
  }
  bones.forEach((bone, name) => {
    const base = rest.get(name)?.clone() ?? bone.quaternion.clone();
    if (isArmBone(name) && grab.get(name)) base.slerp(grab.get(name)!, name.includes("Hand") ? 0.72 : 0.84);
    bone.quaternion.copy(base);
    rest.set(name, base);
  });
  model.updateMatrixWorld(true);
  return rest;
}

function buildSafePillowClip(source: THREE.AnimationClip, basePose: Map<string, THREE.Quaternion>): THREE.AnimationClip {
  const tracks: THREE.KeyframeTrack[] = [];
  for (const track of source.tracks) {
    if (!(track instanceof THREE.QuaternionKeyframeTrack)) continue;
    const boneName = track.name.endsWith(".quaternion") ? track.name.slice(0, -".quaternion".length) : "";
    const base = basePose.get(boneName);
    const weight = animationWeight(source.name, boneName);
    if (!base || weight <= 0) continue;
    const values = new Float32Array(track.values.length);
    const sampled = new THREE.Quaternion();
    const blended = new THREE.Quaternion();
    for (let offset = 0; offset < track.values.length; offset += 4) {
      sampled.fromArray(track.values, offset).normalize();
      blended.copy(base).slerp(sampled, weight).normalize().toArray(values, offset);
    }
    tracks.push(new THREE.QuaternionKeyframeTrack(track.name, track.times, values));
  }
  return new THREE.AnimationClip(source.name, source.duration, tracks);
}

function animationWeight(clipName: string, boneName: string): number {
  const legs = boneName.includes("Thigh") || boneName.includes("Shin") || boneName.includes("Foot");
  const torso = boneName === "PF_Spine2" || boneName === "PF_Chest";
  const head = boneName === "PF_Neck" || boneName === "PF_Head";
  const arms = isArmBone(boneName);
  if (clipName === "PF_Sprint" || clipName === "PF_Jog" || clipName === "PF_Walk") {
    if (legs) return boneName.includes("Thigh") ? 0.42 : 0.48;
    if (torso) return 0.06;
    if (arms) return 0.08;
    return 0;
  }
  if (clipName.startsWith("PF_Jump")) {
    if (legs) return 0.44;
    if (torso) return 0.1;
    if (arms) return 0.18;
    return 0;
  }
  if (clipName.includes("Hit") || clipName.includes("Knock")) {
    if (legs) return 0.28;
    if (torso) return 0.22;
    if (arms) return 0.34;
    if (head) return 0.12;
    return 0;
  }
  if (clipName === "PF_Idle") {
    if (torso) return 0.12;
    if (head) return 0.08;
    if (legs || arms) return 0.06;
  }
  return legs ? 0.3 : torso ? 0.14 : arms ? 0.2 : head ? 0.08 : 0;
}

function isArmBone(name: string): boolean {
  return name.includes("Shoulder") || name.includes("UpperArm") || name.includes("Forearm") || name.includes("Hand");
}
